import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import SecureLS from "secure-ls";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Masonry from "@mui/lab/Masonry";
import GenericViewHero from "./GenericViewHero";
import PrintKitchenCard from "./Functions/PrintKitchenCard";
import PrintKitchenCardMulti from "./Functions/PrintKitchenCardMulti";
import PrintCheckedKitchenCard from "./Functions/PrintCheckedKitchenCard";
import { getAuthCredentials } from "../../utils/auth";
import TitleSetter from "../../component/TitleSetter";
import { createSocket } from "../../utils/socket";

const rawSocket = createSocket();
const socket = rawSocket ?? { on: () => {}, off: () => {}, emit: () => {} };

const ls = new SecureLS();

const KitchenView = () => {
  const [orders, setOrders] = useState([]);
  const [flashingOrders, setFlashingOrders] = useState({});
  const [orderTimers, setOrderTimers] = useState({});
  const [loading, setLoading] = useState(true);
  const [showHistorical, setShowHistorical] = useState(false);
  const [orderTimestamps, setOrderTimestamps] = useState({});
  const [zoomLevel, setZoomLevel] = useState(1);
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(true);
  const [autoPrintCheckedOnly, setAutoPrintCheckedOnly] = useState(false);
  const [mode, setMode] = useState("gross");
  const [selectedDate, setSelectedDate] = useState(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Manila" })
  );
  const autoPrintRef = useRef(true);
  const autoPrintCheckedOnlyRef = useRef(false);

  useEffect(() => {
    const fetchKitchenOrders = async () => {
      try {
        const { token } = getAuthCredentials();
        if (!token) {
          console.error("No auth token found.");
          return;
        }

        const toggleRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/pos/toggles`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (toggleRes.ok) {
          const toggles = await toggleRes.json();
          console.log("toggles: ", toggles);

          const enabled = toggles["auto print kitchen"];
          const printCheckedOnly = toggles["auto print checked items kitchen"];
          setAutoPrintEnabled(typeof enabled === "boolean" ? enabled : true);
          setAutoPrintCheckedOnly(
            typeof printCheckedOnly === "boolean" ? printCheckedOnly : true
          );
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/fetch-orders?viewType=kitchen&date=${selectedDate}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();

        if (response.ok) {
          setMode(data[0]?.mode || "gross");
          const updatedOrders = data.map((order) => ({
            ...order,
            orderNumber: order.orderName || order._id,
          }));
          setOrders(updatedOrders);
          ls.set(
            "orders",
            updatedOrders.map((order) => {
              const d = new Date(order.createdAt);
              return {
                ...order,
                createdAt: isNaN(d.getTime())
                  ? new Date().toISOString()
                  : d.toISOString(),
              };
            })
          );
        } else {
          console.error("Failed to fetch kitchen orders:", data.message);
        }
      } catch (error) {
        console.error("Error fetching kitchen orders:", error);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchKitchenOrders();

    // Refetch every 2 minutes (120,000 milliseconds)
    const intervalId = setInterval(fetchKitchenOrders, 30 * 1000);

    return () => clearInterval(intervalId);
  }, [selectedDate]);

  useEffect(() => {
    autoPrintRef.current = autoPrintEnabled;
  }, [autoPrintEnabled]);

  useEffect(() => {
    autoPrintCheckedOnlyRef.current = autoPrintCheckedOnly;
  }, [autoPrintCheckedOnly]);

  useEffect(() => {
    const savedTimers = ls.get("orderTimers");
    const savedTimestamps = ls.get("orderTimestamps");

    if (savedTimers && savedTimestamps) {
      const currentTime = Date.now();

      const recalculatedTimers = Object.fromEntries(
        Object.entries(savedTimers).map(([orderNumber, time]) => {
          const elapsedTime = Math.floor(
            (currentTime - savedTimestamps[orderNumber]) / 1000
          );
          return [orderNumber, Math.max(time - elapsedTime, 0)];
        })
      );

      setOrderTimers(recalculatedTimers);
    }

    const savedOrders = ls.get("orders");
    if (savedOrders) {
      setOrders(savedOrders);
    }
  }, []);

  useEffect(() => {
    socket.on("connect", () => {
      console.log("Connected to Socket.IO server:", socket.id);
    });

    socket.on("orderList", (orderList) => {
      setOrders(
        orderList.map((o) => ({
          ...o,
          orderNumber: o.orderName || o._id || o.orderId,
        }))
      );
      ls.set("orders", orderList);
    });

    socket.on("newOrder", (order) => {
      const { posId } = getAuthCredentials() || {};
      if (String(order.posId) !== String(posId)) return;
    
      const incoming = {
        ...order,
        orderNumber: order.orderName || order._id || order.orderId,
        status: order.status || "Accepted",
      };
    
      // optional: play sound / auto print
      const bell = new Audio("/assets/sounds/orderarrive2.wav");
      bell.volume = 1.0;
      bell.play();
      if (autoPrintRef.current) {
        PrintKitchenCardMulti(incoming);
      }
    
      setOrders((prev) => {
        const id = incoming._id || incoming.orderId || incoming.orderNumber;
        if (prev.some((o) => (o._id || o.orderId || o.orderNumber) === id)) return prev;
    
        const next = [...prev, incoming];
        ls.set("orders", next);
        return next;
      });
    });

    socket.on("cancelOrder", ({ orderNumber }) => {
      console.log("Received cancelOrder: ", orderNumber);

      setOrders((prevOrders) => {
        const updatedOrders = prevOrders.filter(
          (order) => order.orderNumber !== orderNumber
        );
        return updatedOrders;
      });

      setOrderTimers((prevTimers) => {
        const { [orderNumber]: _, ...remainingTimers } = prevTimers;
        return remainingTimers;
      });

      setFlashingOrders((prevFlashing) => {
        const { [orderNumber]: _, ...remainingFlashing } = prevFlashing;
        return remainingFlashing;
      });

      console.log(
        "Order removed from KitchenView for orderNumber:",
        orderNumber
      );
    });

    return () => {
      socket.off("connect");
      socket.off("orderList");
      socket.off("newOrder");
      socket.off("cancelOrder");
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setOrderTimers(() => {
        const updatedTimers = {};

        orders.forEach((order) => {
          if (!order.orderTime) return;

          const timeRemaining = order.orderTime
            ? Math.floor((new Date(order.orderTime) - Date.now()) / 1000)
            : 0;

          updatedTimers[order.orderNumber] = isNaN(timeRemaining)
            ? 0
            : timeRemaining;

          if (timeRemaining === 0) {
            setFlashingOrders((prevFlashing) => ({
              ...prevFlashing,
              [order.orderNumber]: true,
            }));
          }

          if (timeRemaining === 61) {
            const buzzer = new Audio("/assets/sounds/orderalmostelapse.mp3");
            buzzer.volume = 1.0;
            buzzer.play();

            setFlashingOrders((prevFlashing) => ({
              ...prevFlashing,
              [order.orderNumber]: true,
            }));
          }
        });

        ls.set("orderTimers", updatedTimers);
        return updatedTimers;
      });

      setOrderTimestamps((prevTimestamps) => {
        const updatedTimestamps = {
          ...prevTimestamps,
          ...Object.fromEntries(
            orders.map((order) => [
              order.orderNumber,
              prevTimestamps[order.orderNumber] || Date.now(),
            ])
          ),
        };
        ls.set("orderTimestamps", updatedTimestamps);
        return updatedTimestamps;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [orders]);

  // Update the handler to find the order based on its unique orderNumber
  const handleProcessOrder = async (orderNumber) => {
    const index = orders.findIndex(
      (order) => order.orderNumber === orderNumber
    );
    if (index === -1) return;

    const updatedOrders = [...orders];
    const orderToUpdate = updatedOrders[index];

    try {
      // PrintKitchenCard(orderToUpdate);
      PrintKitchenCardMulti(orderToUpdate);
      const { token } = getAuthCredentials();
      if (!token) {
        console.error("No auth token found.");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/update-order-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderId: orderToUpdate._id || orderToUpdate.orderId,
            status: "Processing",
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        updatedOrders[index] = { ...orderToUpdate, status: "Processing" };
        setOrders(updatedOrders);
        ls.set(
          "orders",
          updatedOrders.map((order) => {
            const d = new Date(order.createdAt);
            return {
              ...order,
              createdAt: isNaN(d.getTime())
                ? new Date().toISOString()
                : d.toISOString(),
            };
          })
        );

        socket.emit("updateOrderStatus", {
          status: "Processing",
          order: updatedOrders[index],
          orderId: orderToUpdate._id || orderToUpdate.orderId,
        });

        console.log(`Order ${orderToUpdate.orderNumber} is now Processing`);
      } else {
        console.error("Failed to update order status:", data.message);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  // Similarly update handleOrderComplete to use orderNumber
  const handleOrderComplete = async (orderId) => {
    const index = orders.findIndex(
      (order) => order._id === orderId || order.orderId === orderId
    );
    if (index === -1) return;

    const updatedOrders = [...orders];
    const orderToUpdate = updatedOrders[index];

    try {
      const { token } = getAuthCredentials();
      if (!token) {
        console.error("No auth token found.");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/update-order-status`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderId: orderToUpdate._id || orderToUpdate.orderId,
            status: "Completed",
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        const completedOrder = {
          ...orderToUpdate,
          status: "Completed",
          updatedAt: new Date().toISOString(),
          BOHorderTime: new Date().toISOString(),
        };

        const newOrdersList = updatedOrders.filter(
          (o) => (o._id || o.orderId) !== orderId
        );

        const historical = [
          ...newOrdersList.filter((o) =>
            [
              "Cancelled",
              "Claimed",
              "Completed",
              "Refunded",
              "Returned",
              "Voided",
            ].includes(o.status)
          ),
          completedOrder,
        ];

        setOrders([...newOrdersList, completedOrder]);
        ls.set("orders", [...newOrdersList, completedOrder]);

        socket.emit("updateOrderStatus", {
          status: "Completed",
          order: completedOrder,
          orderId: completedOrder._id || completedOrder.orderId,
        });

        console.log(`✅ Order ${orderId} marked as Completed`);
      } else {
        console.error("❌ Failed to update order status:", data.message);
      }
    } catch (error) {
      console.error("❌ Error updating order status:", error);
    }
  };

  const toggleItemCompletion = async (
    orderId2,
    menuItemId,
    order,
    optionId = null,
    itemIndex = null,
    optionIndex = null,
    lineItemId = null
  ) => {
    try {
      const { token } = getAuthCredentials();
      if (!token) {
        console.error("No auth token found.");
        return;
      }
  
      const orderId = orderId2 || order._id || order.orderId;
  
      const requestBody = optionId
        ? { orderId, menuItemId, optionId, lineItemId, itemIndex, optionIndex }
        : { orderId, menuItemId, lineItemId, itemIndex };
  
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/order/toggle-item-status`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(requestBody),
        }
      );
  
      const data = await response.json();
      if (response.ok) {
        setOrders((prevOrders) => {
          const updated = prevOrders.map((o) => {
            if (!(o._id === orderId || o.orderId === orderId)) return o;
  
            const apiItem =
              data.order?.items?.find(
                (it) =>
                  (lineItemId && (it._id === lineItemId || it.code === lineItemId)) ||
                  it.menuItemId === menuItemId
              ) || null;
  
            return {
              ...o,
              items: o.items.map((it, idx) => {
                const isSameLine =
                  (lineItemId && (it._id === lineItemId || it.code === lineItemId)) ||
                  (itemIndex !== null && itemIndex === idx);
  
                if (!isSameLine) return it;
  
                if (optionId) {
                  return {
                    ...it,
                    options: (it.options || []).map((opt, k) => {
                      const isSameOption =
                        (optionIndex !== null && optionIndex === k) ||
                        opt.optionId === optionId;
                      if (!isSameOption) return opt;
  
                      const completedFromApi =
                        apiItem?.options?.find((o2) => o2.optionId === optionId)?.completed;
                      return {
                        ...opt,
                        completed:
                          typeof completedFromApi === "boolean"
                            ? completedFromApi
                            : !opt.completed,
                      };
                    }),
                  };
                } else {
                  const completedFromApi =
                    typeof apiItem?.completed === "boolean" ? apiItem.completed : undefined;
                  return {
                    ...it,
                    completed:
                      typeof completedFromApi === "boolean" ? completedFromApi : !it.completed,
                  };
                }
              }),
            };
          });
  
          ls.set(
            "orders",
            updated.map((ord) => {
              const d = new Date(ord.createdAt);
              return {
                ...ord,
                createdAt: isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString(),
              };
            })
          );
  
          return updated;
        });
  
        const updatedItem =
          data.order?.items?.find(
            (i) =>
              (lineItemId && (i._id === lineItemId || i.code === lineItemId)) ||
              i.menuItemId === menuItemId
          ) || null;
  
        const itemCompleted = optionId
          ? updatedItem?.options?.find((o) => o.optionId === optionId)?.completed
          : updatedItem?.completed;
  
        if (!optionId && itemCompleted && Array.isArray(updatedItem?.options) && order.status === "Processing") {
          updatedItem.options.forEach((opt, k) => {
            if (!opt.completed) {
              toggleItemCompletion(
                orderId,
                menuItemId,
                order,
                opt.optionId,
                itemIndex ?? null,
                k,
                lineItemId ?? null
              );
            }
          });
        }
  
        if (autoPrintCheckedOnlyRef.current && itemCompleted && order.status === "Processing") {
          if (!updatedItem) return;
          const partialOrder = {
            ...order,
            items: [
              {
                ...updatedItem,
                options: optionId
                  ? updatedItem.options?.filter((opt) => opt.optionId === optionId)
                  : updatedItem.options,
              },
            ],
          };
          PrintCheckedKitchenCard(partialOrder);
        }
  
        socket.emit("itemStatusUpdated", {
          orderId,
          menuItemId,
          optionId,
          lineItemId,
          itemIndex,
          optionIndex,
          completed: !!itemCompleted,
        });
      } else {
        console.error("Failed to toggle item status:", data.message);
        setOrders((prevOrders) =>
          prevOrders.map((o) =>
            (o._id === orderId || o.orderId === orderId)
              ? {
                  ...o,
                  items: o.items.map((it, idx) => {
                    const isSameLine =
                      (lineItemId && (it._id === lineItemId || it.code === lineItemId)) ||
                      (itemIndex !== null && itemIndex === idx);
                    if (!isSameLine) return it;
                    if (optionId) {
                      return {
                        ...it,
                        options: (it.options || []).map((opt, k) =>
                          (optionIndex !== null && optionIndex === k) || opt.optionId === optionId
                            ? { ...opt, completed: !opt.completed }
                            : opt
                        ),
                      };
                    }
                    return { ...it, completed: !it.completed };
                  }),
                }
              : o
          )
        );
      }
    } catch (error) {
      console.error("Error toggling item status:", error);
    }
  };

  socket.on(
    "itemStatusUpdated",
    ({ orderId, menuItemId, optionId, lineItemId, itemIndex, optionIndex, completed }) => {
      setOrders((prevOrders) =>
        prevOrders.map((order) =>
          order._id === orderId || order.orderId === orderId
            ? {
                ...order,
                items: order.items.map((item, idx) => {
                  const isSameLine =
                    (lineItemId && (item._id === lineItemId || item.code === lineItemId)) ||
                    (itemIndex !== null && itemIndex === idx);
  
                  if (!isSameLine) return item;
  
                  if (optionId) {
                    return {
                      ...item,
                      options: (item.options || []).map((opt, k) =>
                        (optionIndex !== null && optionIndex === k) || opt.optionId === optionId
                          ? { ...opt, completed: completed ?? opt.completed }
                          : opt
                      ),
                    };
                  }
                  return { ...item, completed: completed ?? item.completed };
                }),
              }
            : order
        )
      );
    }
  );

  useEffect(() => {
    socket.on("updateOrderStatus", ({ status, order }) => {
      const { posId } = getAuthCredentials();

      if (order.posId !== posId) {
        console.warn("Update ignored: different POS ID", order);
        return;
      }

      setOrders((prevOrders) => {
        const updatedOrders = prevOrders.filter(
          (o) => o.orderNumber !== order.orderNumber
        );

        if (["Processing", "Completed"].includes(status)) {
          updatedOrders.push({ ...order, status });
        }

        ls.set(
          "orders",
          updatedOrders.map((order) => {
            const d = new Date(order.createdAt);
            return {
              ...order,
              createdAt: isNaN(d.getTime())
                ? new Date().toISOString()
                : d.toISOString(),
            };
          })
        );

        console.log(
          `🔄 Kitchen View: Order ${order.orderNumber} updated to ${status}`
        );
        return updatedOrders;
      });
    });

    return () => {
      socket.off("updateOrderStatus");
    };
  }, []);

  // Filter orders into two columns
  const newOrders = orders.filter((order) => order.status === "Accepted");
  const processingOrders = orders.filter(
    (order) => order.status === "Processing"
  );

  const historicalOrders = orders.filter((order) =>
    [
      "Cancelled",
      "Claimed",
      "Completed",
      "Refunded",
      "Returned",
      "Voided",
    ].includes(order.status)
  );

  const completedOrdersWithTime = historicalOrders.filter(
    (order) =>
      (order.status === "Completed" || order.status === "Claimed") &&
      order.orderTime &&
      order.updatedAt
  );

  const averageOrderTimeSeconds = completedOrdersWithTime.length
    ? Math.round(
        completedOrdersWithTime.reduce((sum, order) => {
          const start = new Date(order.createdAt).getTime();
          const end = new Date(order.BOHorderTime || order.updatedAt).getTime();
          const diffSeconds = Math.max((end - start) / 1000, 0);
          return sum + diffSeconds;
        }, 0) / completedOrdersWithTime.length
      )
    : 0;

  const averageOrderTime = averageOrderTimeSeconds;

  // Reusable component for an order card
  const renderOrderCard = (order) => {
    // Check if non-beverage items are completed
    const allItemsCompleted = order.items
      .filter((item) => !(item.menuTags && item.menuTags.includes("beverage")))
      .every(
        (item) =>
          item.completed &&
          (item.options
            ? item.options
                .filter(
                  (opt) => !(opt.menuTags && opt.menuTags.includes("beverage"))
                )
                .every((opt) => opt.completed)
            : true)
      );
    const hasAnyCompleted = order.items
      .filter((item) => !(item.menuTags && item.menuTags.includes("beverage")))
      .some(
        (item) =>
          item.completed ||
          (item.options
            ? item.options
                .filter((opt) => !(opt.menuTags && opt.menuTags.includes("beverage")))
                .some((opt) => opt.completed)
            : false)
      );

    const isBeverageOnlyOrder =
      order.items.length > 0 &&
      order.items.every((item) => {
        const itemIsBeverage =
          item.menuTags?.includes("beverage") ||
          !item.menuTags ||
          item.menuTags.length === 0;

        const optionsAreBeverage =
          !item.options ||
          item.options.every((opt) => opt.menuTags?.includes("beverage"));

        return itemIsBeverage && optionsAreBeverage;
      });

    const isFreeMeal =
      typeof order.tentNumber === "string" &&
      order.tentNumber.toLowerCase().includes("fmeal");

    return (
      <Box
        key={order.orderNumber}
        sx={{
          width: `${100 / zoomLevel}%`,
          p: 1,
          display: "flex",
        }}
      >
        <TitleSetter title="Kitchen View" />
        <Card
          key={order.orderNumber}
          sx={{
            flex: 1,
            minHeight: 475,
            display: "flex",
            flexDirection: "column",
            transform: `scale(${zoomLevel})`,
            transformOrigin: "top left",
            border:
              orderTimers[order.orderNumber] <= 60
                ? "2px solid red"
                : "1px solid gray",
            backgroundColor:
              orderTimers[order.orderNumber] <= 60 ? "#ffe6e6" : "white",
            transition:
              "transform 0.2s ease, background-color 0.3s, border 0.3s",
          }}
        >
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              backgroundColor:
                order.salesType === "Dine-In"
                  ? "#e8f5e9"
                  : order.salesType === "Delivery"
                  ? "#fff3e0"
                  : "#e1f5fe",
              px: 2,
              py: 1.5,
            }}
          >
            <Box>
              <Typography variant="body1">
                <strong>Order #:</strong> {order.orderNumber}
              </Typography>
              {(() => {
                const d = new Date(order.createdAt);
                if (isNaN(d.getTime())) return null;
                return (
                  <Typography variant="body2" sx={{ mt: -0.5 }}>
                    <strong>Time:</strong>{" "}
                    {d.toLocaleTimeString("en-PH", {
                      hour12: true,
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                    })}
                  </Typography>
                );
              })()}
              <Typography variant="body1">
                <strong>Tent #:</strong> {order.tentNumber}
              </Typography>
              <Typography
                onClick={() => PrintKitchenCard(order)}
                sx={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  "&:hover": {
                    color: "#004ba0",
                  },
                }}
              >
                Print
              </Typography>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: "3rem !important",
                  fontWeight: "bold",
                  color:
                    typeof order.tentNumber === "string" &&
                    order.tentNumber.toLowerCase().includes("fmeal")
                      ? "#c792ea" // light purple
                      : order.salesType === "Dine-In"
                      ? "#2e7d32"
                      : order.salesType === "Delivery"
                      ? "orange"
                      : "#0277bd",
                  lineHeight: 1,
                }}
              >
                {typeof order.tentNumber === "string" &&
                order.tentNumber.toLowerCase().includes("fmeal")
                  ? "FM"
                  : order.salesType === "Dine-In"
                  ? "DI"
                  : order.salesType === "Delivery"
                  ? "DEL"
                  : "TO"}
              </Typography>

              {orderTimers[order.orderNumber] !== undefined &&
                !isNaN(orderTimers[order.orderNumber]) && (
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 0.5,
                      fontWeight: 500,
                      color:
                        orderTimers[order.orderNumber] <= 0
                          ? "red"
                          : "text.secondary",
                    }}
                  >
                    {`${Math.floor(
                      Math.abs(orderTimers[order.orderNumber]) / 60
                    )}:${(Math.abs(orderTimers[order.orderNumber]) % 60)
                      .toString()
                      .padStart(2, "0")}${
                      orderTimers[order.orderNumber] < 0 ? "" : ""
                    }`}
                  </Typography>
                )}
            </Box>
          </Box>
          <CardContent
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              position: "relative",
              p: 2,
              pb: 10,
              fontSize: `${zoomLevel}rem`,
              "& .MuiTypography-root": { fontSize: "inherit" },
              "& .MuiButton-root": { fontSize: "0.875rem" },
            }}
          >
            <Box
              sx={{
                mt: -1.75,
                mb: 2,
                maxHeight: 300, // adjust to taste
                overflowY: "auto",
                px: 0.5,
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                Items:
              </Typography>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  pr: 1,
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {[...order.items]
                    .sort((a, b) => {
                      const aIsBeverage = a.menuTags?.includes("beverage");
                      const bIsBeverage = b.menuTags?.includes("beverage");
                      return aIsBeverage - bIsBeverage;
                    })
                    .map((item, i) => (
                      <Box key={`${item._id || item.code || item.menuItemId || "line"}-${i}`} sx={{ ml: -0.95 }}>
                        {item.menuTags?.includes("beverage") ? (
                          <Typography
                            sx={{
                              fontSize: "0.880rem !important",
                              color: item.completed ? "green" : "inherit",
                              textDecoration: item.completed
                                ? "line-through"
                                : "none",
                              ml: 1,
                            }}
                          >
                            {`${item.amount} pc/s`}
                            {item.code ? ` [${item.code.slice(0, 6)}]` : ""}
                            {item.uniqueName ? (
                              <span style={{ color: "red" }}>
                                {" "}
                                {item.uniqueName}
                              </span>
                            ) : null}{" "}
                            - {item.name}
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Checkbox
                              checked={item.completed || false}
                              onChange={() =>
                                toggleItemCompletion(
                                  order._id,
                                  item.menuItemId,
                                  order,
                                  null,             
                                  i,                 
                                  null,             
                                  item._id || item.code || `${item.menuItemId}-${i}`
                                )
                              }
                              disabled={order.status !== "Processing"}
                              sx={{ transform: "scale(1.5)" }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.880rem !important",
                                color: item.completed ? "green" : "inherit",
                                textDecoration: item.completed
                                  ? "line-through"
                                  : "none",
                              }}
                            >
                              {`${item.amount} pc/s`}
                              {item.code ? ` [${item.code.slice(0, 6)}]` : ""}
                              {item.uniqueName ? (
                                <span style={{ color: "red" }}>
                                  {" "}
                                  {item.uniqueName}
                                </span>
                              ) : null}{" "}
                              - {item.name}
                            </Typography>
                          </Box>
                        )}

                        {[...(item.options ?? [])]
                          .sort((a, b) => {
                            const aIsBeverage =
                              a.menuTags?.includes("beverage");
                            const bIsBeverage =
                              b.menuTags?.includes("beverage");
                            return aIsBeverage - bIsBeverage;
                          })
                          .map((option, j) => (
                            <Box key={`${item._id || item.code || item.menuItemId || "line"}-${i}-opt-${j}`} sx={{ ml: 4 }}>
                              {option.menuTags?.includes("beverage") ? (
                                <Typography
                                  sx={{
                                    fontSize: "0.880rem !important",
                                    color: option.completed
                                      ? "green"
                                      : "inherit",
                                    textDecoration: option.completed
                                      ? "line-through"
                                      : "none",
                                  }}
                                >
                                  -{" "}
                                  {`${option.name}: ${option.choices.join(
                                    ", "
                                  )}`}
                                </Typography>
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Checkbox
                                    checked={option.completed || false}
                                    onChange={() =>
                                      toggleItemCompletion(
                                        order._id,
                                        item.menuItemId,
                                        order,
                                        option.optionId,  
                                        i,                 
                                        j,                 
                                        item._id || item.code || `${item.menuItemId}-${i}` 
                                      )
                                    }
                                    disabled={order.status !== "Processing"}
                                    sx={{ transform: "scale(1.5)" }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "0.880rem !important",
                                      color: option.completed
                                        ? "green"
                                        : "inherit",
                                      textDecoration: option.completed
                                        ? "line-through"
                                        : "none",
                                    }}
                                  >
                                    {`${option.name}: ${option.choices.join(
                                      ", "
                                    )}`}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          ))}
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>

            {/* <Typography variant="body1">
              Total: PHP {order.total.toFixed(2)}
            </Typography> */}
            {(isFreeMeal || !isBeverageOnlyOrder) && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: 16,
                  right: 16,
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  onClick={() =>
                    handleOrderComplete(order.orderId || order._id)
                  }
                  disabled={!hasAnyCompleted || order.status !== "Processing"}
                  sx={{
                    borderRadius: "12px", // Rounded corners
                  }}
                >
                  Order Complete
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  };

  const ZoomControls = () => (
    <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setZoomLevel((z) => Math.max(0.5, z - 0.1))}
      >
        Zoom Out
      </Button>
      <Button
        variant="outlined"
        size="small"
        onClick={() => setZoomLevel((z) => Math.min(2, z + 0.1))}
      >
        Zoom In
      </Button>
      <Typography variant="body2" sx={{ alignSelf: "center" }}>
        {Math.round(zoomLevel * 100)}%
      </Typography>
    </Box>
  );

  const allNonVoidedOrders = orders.filter(
    (order) => order.status !== "Voided"
  );

  const firstShiftOrder = allNonVoidedOrders.find((order) => order.shift);
  const grossSales = firstShiftOrder
    ? mode === "net"
      ? firstShiftOrder.currentNetAmt
      : firstShiftOrder.currentAmt
    : 0;

  return (
    <Box
      sx={{
        flexGrow: 1,
        padding: 3,
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        overflowX: "hidden",
      }}
    >
      <GenericViewHero
        title="Kitchen View"
        ongoingCount={processingOrders.length}
        averageOrderTime={averageOrderTime}
        date={selectedDate}
        onDateChange={(newDate) => {
          setSelectedDate(newDate);
        }}
        grossSales={grossSales}
        mode={mode}
      />

      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Typography variant="h6" align="center" sx={{ mt: 5 }}>
          No Orders
        </Typography>
      ) : (
        <>
          <Box
            sx={{ maxHeight: "85vh", flexGrow: 1, overflowY: "auto", mt: -4 }}
          >
            <Typography variant="h5" align="center" sx={{ mb: 2 }}>
              Processing Orders
            </Typography>
            <Box sx={{ px: 1 }}>
              <Masonry columns={4} spacing={2}>
                {processingOrders.map((order) => (
                  <Box
                    key={order.orderNumber}
                    sx={{
                      transform: `scale(${zoomLevel})`,
                      transformOrigin: "top left",
                    }}
                  >
                    {renderOrderCard(order)}
                  </Box>
                ))}
              </Masonry>
            </Box>
          </Box>

          {historicalOrders.length > 0 && (
            <Card sx={{ mt: 4 }}>
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    cursor: "pointer",
                    mb: 2,
                  }}
                  onClick={() => setShowHistorical((prev) => !prev)}
                >
                  <Typography variant="h5">Completed Kitchen Orders</Typography>
                  {showHistorical ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </Box>

                {showHistorical && (
                  <Box
                    sx={{
                      overflowY: "auto",
                      overflowX: "auto",
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Date/Time</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Order #</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Tent #</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Items</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Sales Type</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Serving Time</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...historicalOrders]
                          .sort(
                            (a, b) =>
                              new Date(b.updatedAt) - new Date(a.updatedAt)
                          )
                          .map((order, index) => (
                            <TableRow
                              key={index}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0, 0, 0, 0.05)",
                                },
                              }}
                            >
                              <TableCell>
                                {new Date(order.updatedAt).toLocaleString()}
                              </TableCell>
                              <TableCell>{order.orderName}</TableCell>
                              <TableCell>{order.tentNumber || "-"}</TableCell>
                              <TableCell>
                                <ul style={{ paddingLeft: 16, margin: 0 }}>
                                  {order.items.map((item, i) => (
                                    <li key={i}>
                                      <span
                                        style={{
                                          textDecoration: item.completed
                                            ? "line-through"
                                            : "none",
                                          color: item.completed
                                            ? "green"
                                            : "inherit",
                                        }}
                                      >
                                        {`${item.amount} pc/s`}
                                        {item.code
                                          ? ` [${item.code.slice(0, 6)}]`
                                          : ""}
                                        {item.uniqueName ? (
                                          <span style={{ color: "red" }}>
                                            {" "}
                                            {item.uniqueName}
                                          </span>
                                        ) : null}{" "}
                                        - {item.name}
                                      </span>
                                      {Array.isArray(item.options) &&
                                        item.options.length > 0 && (
                                          <ul style={{ margin: 0 }}>
                                            {item.options.map((option, j) => (
                                              <li key={j}>
                                                <span
                                                  style={{
                                                    textDecoration:
                                                      option.completed
                                                        ? "line-through"
                                                        : "none",
                                                    color: option.completed
                                                      ? "green"
                                                      : "inherit",
                                                  }}
                                                >
                                                  {`${
                                                    option.name
                                                  }: ${option.choices.join(
                                                    ", "
                                                  )}`}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                    </li>
                                  ))}
                                </ul>
                              </TableCell>
                              <TableCell>{order.salesType}</TableCell>
                              <TableCell>
                                {(() => {
                                  const start = new Date(
                                    order.createdAt
                                  ).getTime();
                                  const end = new Date(
                                    order.BOHorderTime || order.updatedAt
                                  ).getTime();
                                  const seconds = Math.max(
                                    (end - start) / 1000,
                                    0
                                  );
                                  const mins = Math.floor(seconds / 60);
                                  const secs = Math.floor(seconds % 60);
                                  return `${mins}:${secs
                                    .toString()
                                    .padStart(2, "0")}`;
                                })()}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default KitchenView;
